/*
**Project**: Fingerprint Processor Web Application
**Authors**: Michal Vaňo (xvanom00), Jan Svoboda (xsvobo2b)
**Class**:   BIO
**Academic   Year**: 2024/2025
*/

import React, { useRef, useState } from 'react';
import './index.css';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

function App() {
  // State variables to store images and their categories
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [commonStepImages, setCommonStepImages] = useState<string[]>([]);
  const [processorImages, setProcessorImages] = useState<string[]>([]);
  const [classifierImages, setClassifierImages] = useState<string[]>([]);

  // Modal window with example images
  const [isModalOpen, setModalOpen] = useState(false);

  // WebSocket reference for backend communication
  const socketRef = useRef<WebSocket | null>(null);

  // Track the order and count of images for different steps
  const imageOrderRef = useRef<{
    commonSteps: number;
    processor: number;
    classifier: number;
    commonStepsTotal: number;
    processorTotal: number;
    classifierTotal: number;
  }>({ commonSteps: 0, processor: 0, classifier: 0, commonStepsTotal: 0, processorTotal: 0, classifierTotal: 0 });

  const appPath: string = 'https://strade.fit.vutbr.cz/app/fivasvo';

  // Example images
  const exampleImages = [
    { label: 'Left Loop', url: appPath + '/examples/left_loop.jpg' },
    { label: 'Right Loop', url: appPath + '/examples/right_loop.jpg' },
    { label: 'Twin Loop', url: appPath + '/examples/twin_loop.jpg' },
    { label: 'Whorl', url: appPath + '/examples/whorl.jpg' }
  ];

  // Handle image upload by reading the file and sending it to the backend
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file); // Convert file to base64 string
      reader.onloadend = () => {
        setOriginalImage(reader.result as string); // Set the original image for display
        sendImageToBackend(file); // Send the file to the backend
      };
    }
  };

  // Send the uploaded image file to the backend via WebSocket
  const sendImageToBackend = (file: File) => {
    if (!socketRef.current) {
      // Create a new WebSocket connection if not already established
      //socketRef.current = new WebSocket('ws://localhost:7000/ws');  // for local testing
      socketRef.current = new WebSocket('wss://strade.fit.vutbr.cz/app/fivasvo_backend/ws');

      socketRef.current.onopen = () => {
        // Read the file as an array buffer and send it to the backend
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onloadend = () => {
          socketRef.current?.send(reader.result as ArrayBuffer);
        };
      };

      // Handle messages received from the backend
      socketRef.current.onmessage = (event) => {
        if (typeof event.data === "string") {
          // Parse metadata message containing image counts for each step
          const message = JSON.parse(event.data);
          console.log("Received counts message:", message);

          // Update image counts and reset existing images
          imageOrderRef.current.commonStepsTotal = message.num_images_common_steps;
          imageOrderRef.current.processorTotal = message.num_images_processor;
          imageOrderRef.current.classifierTotal = message.num_images_classifier;

          imageOrderRef.current.commonSteps = 0;
          imageOrderRef.current.processor = 0;
          imageOrderRef.current.classifier = 0;

          setCommonStepImages([]);
          setProcessorImages([]);
          setClassifierImages([]);
        } else {
          // Handle incoming image data
          const blob = new Blob([event.data], { type: 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          console.log("Received image:", url);

          // Assign the received image to the appropriate category
          if (imageOrderRef.current.commonSteps < imageOrderRef.current.commonStepsTotal) {
            setCommonStepImages(prevImages => [...prevImages, url]);
            imageOrderRef.current.commonSteps += 1;
            console.log("Assigned to Common Steps");
          } else if (imageOrderRef.current.processor < imageOrderRef.current.processorTotal) {
            setProcessorImages(prevImages => [...prevImages, url]);
            imageOrderRef.current.processor += 1;
            console.log("Assigned to Processor");
          } else if (imageOrderRef.current.classifier < imageOrderRef.current.classifierTotal) {
            setClassifierImages(prevImages => [...prevImages, url]);
            imageOrderRef.current.classifier += 1;
            console.log("Assigned to Classifier");
          } else {
            console.error("Received more images than expected.");
          }
        }
      };
    } else if (socketRef.current.readyState === WebSocket.OPEN) {
      // Send the file if WebSocket connection is already open
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = () => {
        socketRef.current?.send(reader.result as ArrayBuffer);
      };
    }
  };

  // Handle example image selection
  const handleExampleSelect = async (exampleUrl: string) => {
    try {
      const response = await fetch(exampleUrl);
      const blob = await response.blob();

      const file = new File([blob], 'example_image.jpg', { type: blob.type });

      sendImageToBackend(file);

      // Set the imrge as the originalImage
      const reader = new FileReader();
      reader.onloadend = () => {
        setOriginalImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error fetching example image:", error);
    }
  };

  // Download all images as a single zip file
  // This unction was generated by a GPT model
  const downloadAllImages = async () => {
    if (!originalImage) return; // Ensure we have an original image first

    const zip = new JSZip();

    // Add the original image to the zip
    zip.file(`original_image.jpg`, fetch(originalImage).then(res => res.blob()));

    // Add common step images
    commonStepImages.forEach((url, index) => {
      zip.file(`common_step_${index + 1}.jpg`, fetch(url).then(res => res.blob()));
    });

    // Add processor step images
    processorImages.forEach((url, index) => {
      zip.file(`processor_image_${index + 1}.jpg`, fetch(url).then(res => res.blob()));
    });

    // Add classifier step images
    classifierImages.forEach((url, index) => {
      zip.file(`classifier_image_${index + 1}.jpg`, fetch(url).then(res => res.blob()));
    });

    // Generate the zip file and trigger the download
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'all_images.zip');
  };

  return (
    <div className="container">
      <h1>Fingerprint Image Processor</h1>

      <div className="buttons-row">
        {/* Image upload button */}
        <label className="upload-button" htmlFor="file-upload">
          Choose an image
        </label>
        <input
          id="file-upload"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />

        {/* Button to open the example image modal */}
        <button
          className="upload-button"
          onClick={() => setModalOpen(true)}
        >
          Use an example image
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}> {/* Used to stop a click from bubbling up and closing the modal window */}
            <h2>Select an Example Image</h2>
            <div className="thumbnail-container">
              {exampleImages.map((img, index) => (
                <div key={index} className="thumbnail">
                  <img
                    src={img.url}
                    alt={img.label}
                    onClick={() => {
                      handleExampleSelect(img.url);
                      setModalOpen(false);
                    }}
                  />
                  <p style={{ marginTop: '5px', fontSize: '1.2em' }}>{img.label}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setModalOpen(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>

        {/* Display original image */}
        {originalImage && (
          <div>
            <h2>Original Image</h2>
            <div className="image-section">
              <div className="image-container">
                <img src={originalImage} alt="Original" className="image" />
                <a href={originalImage} download="original_image.jpg" className="download-icon">Download</a>
              </div>
            </div>
          </div>
        )}

        {/* Display common step images */}
        {originalImage && (
          <div>
            <h2>Common Steps</h2>
            <div className="image-section">
              {commonStepImages.map((imgUrl, index) => (
                <div key={index} className="image-container">
                  <h4>{getCommonStepsImageTitle(index)}</h4>
                  <img src={imgUrl} alt={`Common Step ${index + 1}`} />
                  <a href={imgUrl} download={`common_step_${index + 1}.jpg`} className="download-icon">Download</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display processor step images */}
        {originalImage && (
          <div>
            <h2>Minutiae Extraction</h2>
            <div className="image-section">
              {processorImages.map((imgUrl, index) => (
                <div key={index} className="image-container">
                  <h4>{getProcessorImageTitle(index)}</h4>
                  <img src={imgUrl} alt={`Processor Image ${index + 1}`} />
                  <a href={imgUrl} download={`processor_image_${index + 1}.jpg`} className="download-icon">Download</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display classifier step images */}
        {originalImage && (
          <div>
            <h2>Type Classification</h2>
            <div className="image-section">
              {classifierImages.map((imgUrl, index) => (
                <div key={index} className="image-container">
                  <h4>{getClassifierImageTitle(index)}</h4>
                  <img src={imgUrl} alt={`Classifier Image ${index + 1}`} />
                  <a href={imgUrl} download={`classifier_image_${index + 1}.jpg`} className="download-icon">Download</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Download All Button */}
      {(processorImages.length > 0 || classifierImages.length > 0) && (
        <button className="download-all-button" onClick={downloadAllImages}>
          Download All
        </button>
      )}

    {/* Footer */}
    <footer style={{ marginTop: '20px', textAlign: 'center', color: '#353535' }}>
      <p>No data is stored on the server.<br/>
      Authors: Michal Vaňo, Jan Svoboda. BUT FIT 2024.</p>
    </footer>

    </div>
  );

  // Get title for common step images
  function getCommonStepsImageTitle(index: number): string {
    const titles = [
      "Preprocessed Image",
      "Orientation Field",
    ];
    return titles[index] || `Image ${index + 1}`;
  }

  // Get title for processor step images
  function getProcessorImageTitle(index: number): string {
    const titles = [
      "Extracted Lines",
      "Thinned Lines",
      "Minutiae",
    ];
    return titles[index] || `Image ${index + 1}`;
  }

  // Get title for classifier step images
  function getClassifierImageTitle(index: number): string {
    const titles = [
      "Final Type"
    ];
    return titles[index] || `Image ${index + 1}`;
  }
}

export default App;
